import axios from '../utils/axios';

// Enhanced Lead Management API endpoints

/**
 * Get leads with advanced filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-based)
 * @param {number} params.limit - Number of items per page
 * @param {string} params.status - Lead status filter
 * @param {string} params.priority - Priority filter (low, medium, high)
 * @param {string} params.assigneeId - Assignee user ID filter
 * @param {string} params.keyword - Search keyword
 * @param {string} params.domain - Domain filter
 * @param {string} params.dateFrom - Date range start (ISO string)
 * @param {string} params.dateTo - Date range end (ISO string)
 * @param {string} params.orderValueMin - Minimum order value
 * @param {string} params.orderValueMax - Maximum order value
 * @param {string} params.sortBy - Sort field (createdAt, orderValue, lastActivity)
 * @param {string} params.sortOrder - Sort order (asc, desc)
 * @returns {Promise} API response with leads data
 */
export const getLeadsAdvanced = async (params = {}) => {
  try {
    const queryString = new URLSearchParams();

    // Add all non-empty parameters to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '' && value !== 'all') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/advanced?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching advanced leads:', error);
    throw error;
  }
};

/**
 * Update lead status
 * @param {number} leadId - Lead ID
 * @param {string} status - New status
 * @param {string} notes - Optional notes about the status change
 * @param {string} priority - Optional priority update (low, medium, high)
 * @returns {Promise} API response
 */
export const updateLeadStatus = async (leadId, status, notes = '', priority = null) => {
  try {
    const payload = {
      status,
      notes,
      timestamp: new Date().toISOString(),
    };

    // Include priority in the update if provided
    if (priority) {
      payload.priority = priority;
    }

    const response = await axios.put(`/api/leads/${leadId}/status`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw error;
  }
};

/**
 * Update lead order value
 * @param {number} leadId - Lead ID
 * @param {number} orderValue - Order value in SEK
 * @param {string} currency - Currency code (default: SEK)
 * @returns {Promise} API response
 */
export const updateLeadOrderValue = async (leadId, orderValue, currency = 'SEK') => {
  try {
    const response = await axios.put(`/api/leads/${leadId}/order-value`, {
      orderValue,
      currency,
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead order value:', error);
    throw error;
  }
};

/**
 * Update lead quoted value (Offererat)
 * @param {number} leadId - Lead ID
 * @param {number} quotedValue - Quoted value in SEK
 * @param {string} currency - Currency code (default: SEK)
 * @returns {Promise} API response
 */
export const updateLeadQuotedValue = async (leadId, quotedValue, currency = 'SEK') => {
  try {
    const response = await axios.put(`/api/leads/${leadId}/quoted-value`, {
      quotedValue,
      currency,
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead quoted value:', error);
    throw error;
  }
};

/**
 * Assign lead to a user
 * @param {number} leadId - Lead ID
 * @param {number} assigneeId - User ID to assign to
 * @param {string} notes - Optional assignment notes
 * @returns {Promise} API response
 */
export const assignLead = async (leadId, assigneeId, notes = '') => {
  try {
    const response = await axios.put(`/api/leads/${leadId}/assign`, {
      assigneeId,
      notes,
      assignedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning lead:', error);
    throw error;
  }
};

/**
 * Update lead priority
 * @param {number} leadId - Lead ID
 * @param {string} priority - Priority level (low, medium, high)
 * @returns {Promise} API response
 */
export const updateLeadPriority = async (leadId, priority) => {
  try {
    const response = await axios.put(`/api/leads/${leadId}/priority`, {
      priority,
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead priority:', error);
    throw error;
  }
};

/**
 * Get available reply methods for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} API response with available reply methods
 */
export const getLeadReplyMethods = async (leadId) => {
  try {
    const response = await axios.get(`/api/leads/${leadId}/reply-methods`);
    return response.data;
  } catch (error) {
    console.error('Error fetching lead reply methods:', error);
    throw error;
  }
};

/**
 * Get conversation messages for a lead
 * @param {number} leadId - Lead ID
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of messages per page
 * @param {boolean} includeInternal - Include internal notes (default: false)
 * @param {string} messageType - Filter by message type (email, sms, call_note)
 * @returns {Promise} API response with conversation data
 */
export const getLeadConversations = async (leadId, page = 0, limit = 50, includeInternal = false, messageType = null) => {
  try {
    const params = { page, limit, includeInternal };

    if (messageType) {
      params.messageType = messageType;
    }

    const response = await axios.get(`/api/leads/${leadId}/conversations`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching lead conversations:', error);
    throw error;
  }
};

/**
 * Send a reply message to a lead
 * @param {number} leadId - Lead ID
 * @param {string} message - Message content
 * @param {Array} attachments - File attachments (optional)
 * @param {string} messageType - Message type (email, sms, call_note)
 * @param {string} subject - Optional subject for emails
 * @param {boolean} isInternal - Whether this is an internal note (default: false)
 * @returns {Promise} API response
 */
export const sendLeadReply = async (leadId, message, attachments = [], messageType = 'email', subject = '', isInternal = false) => {
  try {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('messageType', messageType);
    formData.append('isInternal', isInternal.toString());

    if (subject && messageType === 'email') {
      formData.append('subject', subject);
    }

    // Add attachments if any
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const response = await axios.post(`/api/leads/${leadId}/reply`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error sending lead reply:', error);
    throw error;
  }
};

/**
 * Get lead activity timeline
 * @param {number} leadId - Lead ID
 * @returns {Promise} API response with activity data
 */
export const getLeadActivities = async (leadId) => {
  try {
    const response = await axios.get(`/api/leads/${leadId}/activities`);
    return response.data;
  } catch (error) {
    console.error('Error fetching lead activities:', error);
    throw error;
  }
};

/**
 * Add a note/activity to a lead
 * @param {number} leadId - Lead ID
 * @param {string} activityType - Activity type (note, call, meeting, email)
 * @param {string} title - Activity title
 * @param {string} description - Activity description
 * @param {Object} metadata - Additional metadata
 * @returns {Promise} API response
 */
export const addLeadActivity = async (leadId, activityType, title, description, metadata = {}) => {
  try {
    const response = await axios.post(`/api/leads/${leadId}/activities`, {
      activityType,
      title,
      description,
      metadata,
      timestamp: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error adding lead activity:', error);
    throw error;
  }
};

/**
 * Bulk update lead status (for multiple leads)
 * @param {Array} leadIds - Array of lead IDs
 * @param {string} status - New status
 * @param {string} notes - Optional notes
 * @param {string} priority - Optional priority update (low, medium, high)
 * @returns {Promise} API response
 */
export const bulkUpdateLeadStatus = async (leadIds, status, notes = '', priority = null) => {
  try {
    const payload = {
      leadIds,
      status,
      notes,
      timestamp: new Date().toISOString(),
    };

    // Include priority in the bulk update if provided
    if (priority) {
      payload.priority = priority;
    }

    const response = await axios.put('/api/leads/bulk/status', payload);
    return response.data;
  } catch (error) {
    console.error('Error bulk updating lead status:', error);
    throw error;
  }
};

/**
 * Bulk assign leads to a user
 * @param {Array} leadIds - Array of lead IDs
 * @param {number} assigneeId - User ID to assign to
 * @param {string} notes - Optional assignment notes
 * @returns {Promise} API response
 */
export const bulkAssignLeads = async (leadIds, assigneeId, notes = '') => {
  try {
    const response = await axios.put('/api/leads/bulk/assign', {
      leadIds,
      assigneeId,
      notes,
      assignedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk assigning leads:', error);
    throw error;
  }
};

/**
 * Get comprehensive analytics summary
 * @param {Object} filters - Optional filters for analytics
 * @returns {Promise} API response with analytics data
 */
export const getLeadAnalyticsSummary = async (filters = {}) => {
  try {
    const queryString = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/analytics/summary?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analytics summary:', error);
    throw error;
  }
};

/**
 * Get conversion funnel data
 * @param {Object} filters - Optional filters
 * @returns {Promise} API response with funnel data
 */
export const getLeadAnalyticsFunnel = async (filters = {}) => {
  try {
    const queryString = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/analytics/funnel?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching funnel data:', error);
    throw error;
  }
};

/**
 * Get performance metrics
 * @param {Object} filters - Optional filters
 * @returns {Promise} API response with metrics data
 */
export const getLeadAnalyticsMetrics = async (filters = {}) => {
  try {
    const queryString = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/analytics/metrics?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analytics metrics:', error);
    throw error;
  }
};

/**
 * Mark conversations as read
 * @param {number} leadId - Lead ID
 * @param {Array} conversationIds - Array of conversation IDs to mark as read
 * @returns {Promise} API response
 */
export const markConversationsAsRead = async (leadId, conversationIds = []) => {
  try {
    const response = await axios.put('/api/leads/conversations/mark-read', {
      leadId,
      conversationIds,
      readAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    console.error('Error marking conversations as read:', error);
    throw error;
  }
};

/**
 * Upload files to a lead
 * @param {number} leadId - Lead ID
 * @param {Array} files - Files to upload
 * @param {number} conversationId - Optional conversation ID to associate files with
 * @returns {Promise} API response
 */
export const uploadLeadFiles = async (leadId, files, conversationId = null) => {
  try {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`file_${index}`, file);
    });

    if (conversationId) {
      formData.append('conversationId', conversationId);
    }

    const response = await axios.post(`/api/leads/${leadId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

/**
 * Download a lead attachment
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Blob response for file download
 */
export const downloadLeadAttachment = async (attachmentId) => {
  try {
    const response = await axios.get(`/api/leads/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error downloading attachment:', error);
    throw error;
  }
};

/**
 * Export leads to CSV/Excel
 * @param {Object} filters - Filters to apply to export
 * @param {string} format - Export format (csv, excel)
 * @returns {Promise} Blob response for file download
 */
export const exportLeads = async (filters = {}, format = 'csv') => {
  try {
    const queryString = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    queryString.append('format', format);

    const response = await axios.get(`/api/leads/export?${queryString}`, {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error exporting leads:', error);
    throw error;
  }
};

/**
 * Get available users for lead assignment
 * @returns {Promise} API response with users data
 */
export const getAssignableUsers = async () => {
  try {
    const response = await axios.get('/api/users/assignable');
    return response.data;
  } catch (error) {
    console.error('Error fetching assignable users:', error);
    throw error;
  }
};

/**
 * Get lead conversion funnel data
 * @param {Object} filters - Optional filters
 * @returns {Promise} API response with funnel data
 */
export const getLeadConversionFunnel = async (filters = {}) => {
  try {
    const queryString = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/funnel?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching conversion funnel:', error);
    throw error;
  }
};

/**
 * Search leads with advanced text search
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise} API response with search results
 */
export const searchLeads = async (query, options = {}) => {
  // Extract signal from options for request cancellation
  const { signal, ...restOptions } = options;

  try {
    const response = await axios.post('/api/leads/search', {
      query,
      ...restOptions,
      timestamp: new Date().toISOString(),
    }, {
      // Pass abort signal to axios for request cancellation
      signal,
    });
    return response.data;
  } catch (error) {
    // Re-throw abort errors without logging (expected behavior)
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      throw error;
    }
    console.error('Error searching leads:', error);
    throw error;
  }
};

/**
 * Get lead performance metrics
 * @param {string} period - Time period (week, month, quarter, year)
 * @param {Object} filters - Optional filters
 * @returns {Promise} API response with performance data
 */
export const getLeadPerformanceMetrics = async (period = 'month', filters = {}) => {
  try {
    const queryString = new URLSearchParams();
    queryString.append('period', period);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await axios.get(`/api/leads/metrics?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching performance metrics:', error);
    throw error;
  }
};
